import dayjs from "dayjs";
import { ProcessedAssignment } from "../../type";

interface WeekBar {
  assignment: ProcessedAssignment;
  jobOrderNo: string;
  startCol: number;
  endCol: number;
  lane: number;
  isStartClipped: boolean;
  isEndClipped: boolean;
}

interface CalendarCell {
  date: Date;
  isCurrentMonth: boolean;
}

/**
 * Collect and lane-pack bars from ALL jobs for a given week.
 * Each bar carries the jobOrderNo so it can be rendered with the right color.
 */
export function getAllWeekBars(
  allAssignments: ProcessedAssignment[],
  week: (Date | null)[]
): WeekBar[] {
  const actualDays = week.filter((d): d is Date => d !== null);
  if (actualDays.length === 0) return [];

  const weekStart = dayjs(actualDays[0]).startOf("day");
  const weekEnd = dayjs(actualDays[actualDays.length - 1]).endOf("day");

  const overlapping = allAssignments.filter((a) => {
    if (!a.startDate) return false;
    const aStart = dayjs(a.startDate).startOf("day");
    const aEnd = a.eddDate ? dayjs(a.eddDate).startOf("day") : aStart;
    return !aStart.isAfter(weekEnd) && !aEnd.isBefore(weekStart);
  });

  // Sort: earlier start first, then longer duration first
  overlapping.sort((a, b) => {
    const d = dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf();
    if (d !== 0) return d;
    const aEnd = a.eddDate ? dayjs(a.eddDate) : dayjs(a.startDate);
    const bEnd = b.eddDate ? dayjs(b.eddDate) : dayjs(b.startDate);
    return (
      bEnd.diff(dayjs(b.startDate)) -
      aEnd.diff(dayjs(a.startDate))
    );
  });

  const rawBars: WeekBar[] = overlapping.map((a) => {
    const aStart = dayjs(a.startDate).startOf("day");
    const aEnd = a.eddDate ? dayjs(a.eddDate).startOf("day") : aStart;

    // startCol
    let startCol: number;
    let isStartClipped = false;
    if (aStart.isBefore(weekStart)) {
      startCol = week.findIndex((d) => d !== null);
      isStartClipped = true;
    } else {
      const idx = week.findIndex(
        (d) => d !== null && dayjs(d).startOf("day").isSame(aStart, "day")
      );
      startCol = idx >= 0 ? idx : week.findIndex((d) => d !== null);
    }

    // endCol
    let endCol: number;
    let isEndClipped = false;
    if (aEnd.isAfter(weekEnd.startOf("day"))) {
      endCol =
        week
          .map((d, i) => (d !== null ? i : -1))
          .filter((i) => i >= 0)
          .pop() ?? 6;
      isEndClipped = true;
    } else {
      const idx = week
        .map((d, i) =>
          d !== null && dayjs(d).startOf("day").isSame(aEnd, "day") ? i : -1
        )
        .filter((i) => i >= 0)
        .pop();
      endCol = idx ?? startCol;
    }

    if (endCol < startCol) endCol = startCol;

    return {
      assignment: a,
      jobOrderNo: a.jobOrderNo ?? a.baseJobId ?? "",
      startCol,
      endCol,
      isStartClipped,
      isEndClipped,
      lane: 0
    };
  });

  // Greedy lane packing
  rawBars.forEach((bar, idx) => {
    let lane = 0;
    let placed = false;
    while (!placed) {
      const conflict = rawBars.some(
        (other, oi) =>
          oi < idx &&
          other.lane === lane &&
          bar.startCol <= other.endCol &&
          bar.endCol >= other.startCol
      );
      if (!conflict) {
        bar.lane = lane;
        placed = true;
      } else {
        lane++;
      }
    }
  });

  return rawBars;
}

/** Returns all assignments that overlap a specific calendar day */
export function getAssignmentsForDay(
  assignments: ProcessedAssignment[],
  day: Date
): ProcessedAssignment[] {
  const d = dayjs(day).startOf("day");
  return assignments.filter((a) => {
    if (!a.startDate) return false;
    const aStart = dayjs(a.startDate).startOf("day");
    const aEnd = a.eddDate ? dayjs(a.eddDate).startOf("day") : aStart;
    return !aStart.isAfter(d) && !aEnd.isBefore(d);
  });
}

/**
 * Same week grid as splitIntoWeeks but fills padding slots with real
 * prev / next-month dates instead of null, so the calendar can display them.
 */
export function buildWeeksWithFill(days: Date[]): CalendarCell[][] {
  if (days.length === 0) return [];
  const firstDay = dayjs(days[0]);
  const firstDow = firstDay.day(); // 0 = Sunday

  // Prev-month fill
  const prevFill: CalendarCell[] = Array.from({ length: firstDow }, (_, i) => ({
    date: firstDay.subtract(firstDow - i, "day").toDate(),
    isCurrentMonth: false
  }));

  const current: CalendarCell[] = days.map((d) => ({
    date: d,
    isCurrentMonth: true
  }));

  const combined = [...prevFill, ...current];

  // Next-month fill to complete the last week
  const remainder = combined.length % 7;
  if (remainder !== 0) {
    const lastDay = dayjs(days[days.length - 1]);
    for (let i = 1; i <= 7 - remainder; i++) {
      combined.push({
        date: lastDay.add(i, "day").toDate(),
        isCurrentMonth: false
      });
    }
  }

  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < combined.length; i += 7) {
    weeks.push(combined.slice(i, i + 7));
  }
  return weeks;
}

/** Pad to nearest Sunday, then chunk into 7-day weeks */
export function splitIntoWeeks(days: Date[]): (Date | null)[][] {
  if (days.length === 0) return [];
  const firstDow = dayjs(days[0]).day(); // 0 = Sunday
  const padded: (Date | null)[] = [...Array(firstDow).fill(null), ...days];
  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < padded.length; i += 7) {
    const chunk = padded.slice(i, i + 7);
    while (chunk.length < 7) chunk.push(null);
    weeks.push(chunk);
  }
  return weeks;
}

export function priorityColors(priority: number | null | undefined) {
  if (priority === 2)
    return {
      bg: "aero:bg-yellow-50",
      border: "aero:border-yellow-500",
      accent: "aero:bg-yellow-500",
      text: "aero:text-yellow-900"
    };
  if (priority === 3)
    return {
      bg: "aero:bg-red-50",
      border: "aero:border-red-500",
      accent: "aero:bg-red-500",
      text: "aero:text-red-900"
    };
  return {
    bg: "aero:bg-green-100",
    border: "aero:border-green-500",
    accent: "aero:bg-green-500",
    text: "aero:text-green-600"
  };
}

export function fmt(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return dayjs(dateStr).format("DD/MM HH:mm");
}

// Format duration: 1 work day = 9 hours; show "days" if ≥ 9h, else "hours"

export function formatDuration(h: number | null | undefined): string | null {
  if (!h) return null;
  const WORK_HOURS_PER_DAY = 9;
  if (h >= WORK_HOURS_PER_DAY) {
    const days = Math.round(h / WORK_HOURS_PER_DAY);
    return `(${days} ${days === 1 ? "day" : "days"})`;
  }
  const hours = Math.round(h);
  return `(${hours} ${hours === 1 ? "hour" : "hours"})`;
}
